import {useCallback, useRef, useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";

import {API} from "api";
import {
  getChannelSettingsQueryKey,
  type ChannelSettings,
} from "hooks/useChannelSettings";
import {layoutOrderKey} from "libs/selectorVisibility";

/** One write of the layout order: a product, or every product (`*`), and a set. */
export interface LayoutOrderChange {
  product: string;
  set?: string;
  /** The layout ids in selector order. Empty: the wider scope's order again. */
  order: number[];
}

/**
 * The write of the Layout selector order of `channel` (`layoutOrders`,
 * libs/selectorVisibility.ts). A save shows at once, and the writes go to the
 * server one at a time in the order they were made: the last drag is the
 * order that stays.
 */
export function useSaveLayoutOrder(channel: string): {
  save: (change: LayoutOrderChange) => void;
  hasFailed: boolean;
} {
  const queryClient = useQueryClient();
  const pending = useRef(0);
  const [hasFailed, setHasFailed] = useState(false);

  const {mutate} = useMutation({
    // One queue for every layout order: a later write never overtakes an earlier one.
    scope: {id: "layout-order"},
    mutationFn: (change: LayoutOrderChange) => API.putLayoutOrder({channel, ...change}),
    onMutate: async (change) => {
      pending.current += 1;
      setHasFailed(false);
      const queryKey = getChannelSettingsQueryKey(channel);
      await queryClient.cancelQueries({queryKey});
      queryClient.setQueryData<ChannelSettings>(queryKey, (current) => {
        if (!current) return current;
        const key = layoutOrderKey(change.product, change.set);
        const {[key]: _previous, ...rest} = current.layoutOrders ?? {};
        return {
          ...current,
          layoutOrders: change.order.length > 0 ? {...rest, [key]: change.order} : rest,
        };
      });
    },
    onError: (err) => {
      console.error("[useSaveLayoutOrder] Save failed:", err);
      setHasFailed(true);
    },
    onSettled: () => {
      // Read the server's settings back when no other write is queued: an
      // earlier answer must not undo a later drag on the screen.
      pending.current -= 1;
      if (pending.current > 0) return undefined;
      return queryClient.invalidateQueries({queryKey: getChannelSettingsQueryKey(channel)});
    },
  });

  const save = useCallback((change: LayoutOrderChange) => mutate(change), [mutate]);
  return {save, hasFailed};
}
